import { useState, useEffect, useMemo } from 'react';
import type { CurrencyListItem, EntityMatch } from '@acitra/shared';
import { listCurrencies, matchCurrency, unmatchCurrency } from '../../services/api';

interface CurrencySelectorProps {
  tenantId: string;
  invoiceId: string;
  /** Extracted currency code from the invoice (e.g., "USD", "CAD") */
  extractedCurrency?: string;
  currentMatch?: EntityMatch;
  onMatchChange: (match: EntityMatch | undefined) => void;
  disabled?: boolean;
  className?: string; // Add className prop
}

export function CurrencySelector({
  tenantId,
  invoiceId,
  extractedCurrency,
  currentMatch,
  onMatchChange,
  disabled = false,
  className = '', // Default to empty string
}: CurrencySelectorProps) {
  const [currencies, setCurrencies] = useState<CurrencyListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [isMatching, setIsMatching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);

  // Load currencies on mount
  useEffect(() => {
    async function loadCurrencies() {
      try {
        setIsLoading(true);
        const data = await listCurrencies(tenantId, { active: true });
        setCurrencies(data);
      } catch (err) {
        console.error('Failed to load currencies:', err);
        setError('Failed to load currencies');
      } finally {
        setIsLoading(false);
      }
    }

    loadCurrencies();
  }, [tenantId]);

  // Find best auto-match based on currency code
  const suggestedCurrency = useMemo(() => {
    if (!extractedCurrency || currencies.length === 0) {
      return null;
    }

    // Find exact match on currency code (case-insensitive)
    const normalizedExtracted = extractedCurrency.toUpperCase().trim();
    return currencies.find((c) => c.code.toUpperCase() === normalizedExtracted) || null;
  }, [currencies, extractedCurrency]);

  // Sort currencies: suggested first, then alphabetically by code
  const sortedCurrencies = useMemo(() => {
    return [...currencies].sort((a, b) => {
      // Suggested currency first
      if (suggestedCurrency?.id === a.id) return -1;
      if (suggestedCurrency?.id === b.id) return 1;
      // Then alphabetically by code
      return a.code.localeCompare(b.code);
    });
  }, [currencies, suggestedCurrency]);

  const handleSelect = async (currency: CurrencyListItem) => {
    // Close dropdown immediately for better UX
    setIsOpen(false);
    setIsMatching(true);
    setError(null);

    try {
      const response = await matchCurrency(tenantId, invoiceId, { currencyId: currency.id });
      onMatchChange(response.currencyMatch);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 2000);
    } catch (err) {
      console.error('Failed to match currency:', err);
      setError('Failed to match currency');
      setShowError(true);
      setTimeout(() => setShowError(false), 2000);
    } finally {
      setIsMatching(false);
    }
  };

  const handleUnmatch = async () => {
    setIsMatching(true);
    setError(null);

    try {
      await unmatchCurrency(tenantId, invoiceId);
      onMatchChange(undefined);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 2000);
    } catch (err) {
      console.error('Failed to unmatch currency:', err);
      setError('Failed to remove match');
      setShowError(true);
      setTimeout(() => setShowError(false), 2000);
    } finally {
      setIsMatching(false);
    }
  };

  // Format currency for display (just the code now)
  const formatCurrencyDisplay = (currency: CurrencyListItem) => {
    return currency.code;
  };

  // Determine display state
  const hasExtractedCurrency = !!extractedCurrency;
  const isMatched = !!currentMatch;
  const matchedCurrency = isMatched ? currencies.find((c) => c.id === currentMatch.entityId) : null;

  // Status styles (matching EditableValue)
  const statusStyles = showSuccess ? 'border-primary-500' : showError ? 'border-red-500' : '';
  const savingStyles = isMatching
    ? 'border-primary-500 shadow-[0_0_0_3px_rgba(44,160,28,0.4)] animate-pulse'
    : '';
  const hasStatus = showSuccess || showError || isMatching;
  const borderStyle = hasStatus ? '' : 'border-transparent hover:border-gray-300';

  return (
    <div className="relative">
      {/* Current value display / dropdown trigger */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled || isLoading || isMatching}
        className={`w-full flex items-center py-1.5 px-2 text-sm font-medium rounded-md border transition-all duration-200 ${borderStyle} ${savingStyles} ${statusStyles} ${
          disabled || isLoading || isMatching ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
        }`}
      >
        {/* Dropdown arrow on left */}
        <svg
          className={`w-3 h-3 text-gray-400 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>

        {/* Value - right aligned */}
        <span className="flex-1 text-right truncate ml-1">
          {isLoading ? (
            <span className="text-gray-400">Loading...</span>
          ) : isMatched ? (
            <span className={className || 'text-gray-700'}>
              {matchedCurrency?.code || currentMatch.entityName}
            </span>
          ) : hasExtractedCurrency ? (
            <span className="text-gray-700">{extractedCurrency}</span>
          ) : (
            <span className="text-gray-400">-</span>
          )}
        </span>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />

          {/* Dropdown menu */}
          <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
            {/* Matched currency with unmatch option */}
            {isMatched && (
              <div className="px-3 py-2 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700 font-medium">
                    Matched: {currentMatch.entityName}
                  </span>
                  <button
                    type="button"
                    onClick={handleUnmatch}
                    disabled={isMatching}
                    className="text-xs text-red-600 hover:text-red-800 disabled:opacity-50"
                  >
                    Remove
                  </button>
                </div>
              </div>
            )}

            {/* Currencies list */}
            {sortedCurrencies.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500">
                No currencies available (multi-currency may not be enabled)
              </div>
            ) : (
              sortedCurrencies.map((currency) => (
                <button
                  key={currency.id}
                  type="button"
                  onClick={() => handleSelect(currency)}
                  disabled={isMatching || (isMatched && currency.id === currentMatch.entityId)}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 disabled:opacity-50 flex items-center justify-between"
                >
                  <span>{formatCurrencyDisplay(currency)}</span>
                  {suggestedCurrency?.id === currency.id && !isMatched && (
                    <span className="text-xs text-amber-600 bg-amber-100 px-1.5 py-0.5 rounded">
                      Suggested
                    </span>
                  )}
                </button>
              ))
            )}
          </div>
        </>
      )}

      {/* Error message */}
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}
